import { css } from "@compiled/react";
import { useInView } from "react-intersection-observer";
import { useIntl } from "react-intl";

import { media } from "@atlaskit/primitives/responsive";
import { token } from "@atlaskit/tokens";
import UFOSegment from "@atlassian/wac-ufo-segment";

import Card from "@/components/wpl/card/src";
import type {
    IndividualTabPropsCustom,
    TabColorProps
} from "@/components/wpl/tab-group/src";
import TabGroup from "@/components/wpl/tab-group/src";
import { useLocalizedHref } from "@/libraries/web-framework";
import { Logo, Row } from "@/libraries/wpl/foundations/src";

import Solutions_1_Con_Trello_JWM from "./solutions_1_Con_Trello_JWM";
import Solutions_2_JSM from "./solutions_2_JSM";
import Solutions_3_Jira_JiraAlign_Compass from "./solutions_3_Jira_Jira-align_Compass";

const carouselCss = css({
    "padding": `${token("space.200")} ${token("space.0")} ${token(
        "space.200"
    )} !important`,
    "overflowX": "hidden",
    "h2": {
        color: "#253858",
        fontWeight: 500,
        marginBlockStart: token("space.0"),
        textAlign: "center",
        [media.above.sm]: {
            fontSize: "2.25rem",
            lineHeight: 1.33
        },
        [media.below.sm]: {
            letterSpacing: 0,
            fontSize: "1.5rem",
            lineHeight: 1.33333333,
            marginBlockEnd: "14px"
        }
    },
    ".row-layout .tab-group-inner": {
        gap: token("space.400"),
        width: "100%",
        maxWidth: "1020px",
        margin: "0 auto"
    },
    ".tab-panel .row-layout": {
        paddingBlockStart: token("space.800")
    },
    [media.below.sm]: {
        ".row-layout > div:nth-child(2)": {
            order: -1
        }
    },
    "button": {
        borderColor: "transparent !important",
        flex: 1,
        fontWeight: 700,
        padding: `${token("space.400")} 0`
    },
    [`.tab-group div[role="tablist"]`]: {
        maxWidth: "1200px",
        margin: "auto"
    },
    ".tab-group .row-layout": {
        "> .tab-group-inner": {
            gap: "1rem",
            button: {
                padding: "10px 15px",
                letterSpacing: "0.3px",
                minHeight: "90px",
                lineHeight: 1,
                [media.below.md]: {
                    fontSize: "1.25rem"
                }
            }
        }
    },
    ".tab-panels": {
        "> .tab-panel": {
            "marginBlockStart": token("space.0"),
            "> .row-layout": {
                "> div": {
                    "paddingBlockStart": token("space.200"),
                    "paddingBlockEnd": token("space.200"),
                    "paddingInlineStart": 0,
                    "paddingInlineEnd": 0,
                    "position": "relative",
                    "minHeight": "1px",
                    "alignSelf": "flex-start",
                    "width": "100%",
                    "> a": {
                        marginBlockStart: "15px",
                        marginBlockEnd: "36px"
                    }
                },
                ".content-block": {
                    "> .text-section > h3": {
                        fontWeight: 500,
                        [media.above.sm]: {
                            marginBlockEnd: token("space.200"),
                            lineHeight: 1.33333333,
                            fontSize: "1.5rem"
                        },
                        [media.below.sm]: {
                            marginBlockEnd: "14px",
                            lineHeight: 1.2,
                            fontSize: "1.25rem",
                            letterSpacing: "0.3px"
                        }
                    },
                    ".contentBlock-linkFlex > .link-wrapper > a": {
                        "fontWeight": "inherit",
                        "marginBlockStart": 0,
                        "&:hover, &:active": {
                            textDecoration: "underline",
                            svg: {
                                transform: "translate3d(5px, 0, 0)",
                                transition: "transform 250ms"
                            }
                        }
                    }
                }
            }
        }
    },
    ".select-container": {
        "padding-block-start": "10px",
        "select#carouseltabs": {
            fontSize: "1rem"
        }
    },
    ".fade-in": {
        opacity: 0,
        transition: "opacity 0.5s ease"
    },
    ".in-view": {
        opacity: 1
    }
});

const leftCardCss = css({
    "padding": token("space.200"),
    "> div": {
        boxShadow: "none"
    },
    ".content-block .text-section": {
        padding: token("space.0")
    },
    "h3": {
        color: "#253858"
    }
});

const rightColumnCss = css({
    svg: {
        height: "auto",
        width: "100%",
        [media.above.md]: {
            width: "120%"
        }
    }
});

const productDescriptionCss = css({
    "display": "flex",
    "margin": `${token("space.300")} 0 36px`,
    "> div:first-of-type": {
        alignItems: "center",
        background: "#dfe1e5",
        display: "flex",
        height: "72px",
        justifyContent: "center",
        minWidth: "72px",
        transition: "all 150ms ease-in-out",
        borderRadius: "3px"
    },
    "&:hover": {
        "> div:first-of-type": {
            background: "#f4f5f7",
            boxShadow: "0 2px 14px 0 rgba(9, 31, 65, 0.2)"
        },
        "> div:last-of-type h4": {
            color: "#0052cc"
        }
    },
    "> div:last-of-type": {
        "display": "flex",
        "flexDirection": "column",
        "justifyContent": "center",
        "marginInlineStart": token("space.300"),
        "borderRadius": "3px",
        "h4, p": {
            margin: 0
        },
        "h4": {
            fontFamily: "var(--bxp-charlie-text-font-family)",
            fontSize: token("space.200"),
            fontWeight: 600,
            letterSpacing: "0.3px"
        }
    }
});

const carouselTabColors: TabColorProps = {
    defaultBG: "#deebff",
    defaultText: "#0052cc",
    hoverBG: "#deebff",
    hoverText: "#0052cc",
    activeBG: "#0065ff",
    activeText: "#ffffff"
};

const ProductDescription = ({
    icon,
    name,
    description,
    url
}: {
    icon: JSX.Element;
    name: string;
    description: string;
    url: string;
}) => (
    <a href={url} css={productDescriptionCss}>
        <div>{icon}</div>
        <div>
            <h4>{name}</h4>
            <p>{description}</p>
        </div>
    </a>
);

export const Carousel = () => {
    const intl = useIntl();

    const [wmRef, wmInView] = useInView({
        triggerOnce: false
    });
    const wmClass = `fade-in ${wmInView ? "in-view" : ""}`;

    const [itsmRef, itsmInView] = useInView({
        triggerOnce: false
    });
    const itsmClass = `fade-in ${itsmInView ? "in-view" : ""}`;

    const [adoRef, adoInView] = useInView({
        triggerOnce: false
    });
    const adoClass = `fade-in ${adoInView ? "in-view" : ""}`;

    const wmProducts: React.ComponentProps<typeof ProductDescription>[] = [
        {
            icon: <Logo brand="confluence" type="mark" appearance="brand" />,
            name: intl.formatMessage({
                id: "carousel.product.name.confluence",
                defaultMessage: "Confluence"
            }),
            description: intl.formatMessage({
                id: "carousel.product.description.confluence",
                defaultMessage: "Content collaboration"
            }),
            url: useLocalizedHref("/software/confluence")
        },
        {
            icon: <Logo brand="trello" type="mark" appearance="brand" />,
            name: intl.formatMessage({
                id: "carousel.product.name.trello",
                defaultMessage: "Trello"
            }),
            description: intl.formatMessage({
                id: "carousel.product.description.trello",
                defaultMessage: "Visual project management"
            }),
            url: "https://trello.com/home"
        },
        {
            icon: (
                <Logo
                    brand="jiraworkmanagement"
                    type="mark"
                    appearance="brand"
                />
            ),
            name: intl.formatMessage({
                id: "carousel.product.name.jira-work-management",
                defaultMessage: "Jira Work Management"
            }),
            description: intl.formatMessage({
                id: "carousel.product.description.jira-work-management",
                defaultMessage: "Business team collaboration"
            }),
            url: useLocalizedHref("/software/jira/work-management")
        },
        {
            icon: <Logo brand="atlas" type="mark" appearance="brand" />,
            name: intl.formatMessage({
                id: "carousel.product.name.atlas",
                defaultMessage: "Atlas"
            }),
            description: intl.formatMessage({
                id: "carousel.product.description.atlas",
                defaultMessage: "Teamwork directory"
            }),
            url: useLocalizedHref("/software/atlas")
        }
    ];

    const itsmProducts: React.ComponentProps<typeof ProductDescription>[] = [
        {
            icon: (
                <Logo
                    brand="jiraservicemanagement"
                    type="mark"
                    appearance="brand"
                />
            ),
            name: intl.formatMessage({
                id: "carousel.product.name.jira-service-management",
                defaultMessage: "Jira Service Management"
            }),
            description: intl.formatMessage({
                id: "carousel.product.description.jira-service-management",
                defaultMessage: "High-velocity ITSM"
            }),
            url: useLocalizedHref("/software/jira/service-management")
        }
    ];

    const adoProducts: React.ComponentProps<typeof ProductDescription>[] = [
        {
            icon: <Logo brand="jirasoftware" type="mark" appearance="brand" />,
            name: intl.formatMessage({
                id: "carousel.product.name.jira-software",
                defaultMessage: "Jira Software"
            }),
            description: intl.formatMessage({
                id: "carousel.product.description.jira-software",
                defaultMessage: "Project and issue tracking"
            }),
            url: useLocalizedHref("/software/jira")
        },
        {
            icon: (
                <Logo
                    brand="jiraproductdiscovery"
                    type="mark"
                    appearance="brand"
                />
            ),
            name: intl.formatMessage({
                id: "carousel.product.name.jira-product-discovery",
                defaultMessage: "Jira Product Discovery"
            }),
            description: intl.formatMessage({
                id: "carousel.product.description.jira-product-discovery",
                defaultMessage: "Prioritization and roadmapping"
            }),
            url: useLocalizedHref("/software/jira/product-discovery")
        },
        {
            icon: <Logo brand="jiraalign" type="mark" appearance="brand" />,
            name: intl.formatMessage({
                id: "carousel.product.name.jira-align",
                defaultMessage: "Jira Align"
            }),
            description: intl.formatMessage({
                id: "carousel.product.description.jira-align",
                defaultMessage: "Enterprise Agile planning"
            }),
            url: useLocalizedHref("/software/jira/align")
        },
        {
            icon: <Logo brand="compass" type="mark" appearance="brand" />,
            name: intl.formatMessage({
                id: "carousel.product.name.compass",
                defaultMessage: "Compass"
            }),
            description: intl.formatMessage({
                id: "carousel.product.description.compass",
                defaultMessage: "Developer experience platform"
            }),
            url: useLocalizedHref("/software/compass")
        },
        {
            icon: <Logo brand="bitbucket" type="mark" appearance="brand" />,
            name: intl.formatMessage({
                id: "carousel.product.name.bitbucket",
                defaultMessage: "Bitbucket"
            }),
            description: intl.formatMessage({
                id: "carousel.product.description.bitbucket",
                defaultMessage: "Code & CI/CD"
            }),
            url: useLocalizedHref("/software/bitbucket")
        }
    ];

    const carouselTabs: IndividualTabPropsCustom[] = [
        {
            textLabel: intl.formatMessage({
                id: "homepage.carousel.tab-label.work-management",
                defaultMessage: "Work Management"
            }),
            id: "work-management",
            component: (
                <Row layout="4-8">
                    <div css={leftCardCss} ref={wmRef} className={wmClass}>
                        <Card
                            bodyText=""
                            headerText={intl.formatMessage({
                                id: "homepage.carousel.wm-header",
                                defaultMessage:
                                    "Make work flow across teams while connecting back to company goals"
                            })}
                            headerType="h3"
                            ctaProps={{
                                primaryLinkProps: {
                                    text: intl.formatMessage({
                                        id: "homepage.carousel.wm-cta",
                                        defaultMessage:
                                            "Work differently, together"
                                    }),
                                    href: useLocalizedHref(
                                        "/solutions/work-management"
                                    )
                                }
                            }}
                        />
                        {wmProducts.map((props, index) => (
                            <ProductDescription {...props} key={index} />
                        ))}
                    </div>
                    <div css={rightColumnCss} ref={wmRef} className={wmClass}>
                        <Solutions_1_Con_Trello_JWM />
                    </div>
                </Row>
            )
        },
        {
            textLabel: intl.formatMessage({
                id: "homepage.carousel.tab-label.itsm",
                defaultMessage: "IT Service Management"
            }),
            id: "it-service-management",
            component: (
                <Row layout="4-8">
                    <div css={leftCardCss} ref={itsmRef} className={itsmClass}>
                        <Card
                            bodyText=""
                            headerText={intl.formatMessage({
                                id: "homepage.carousel.itsm-header",
                                defaultMessage:
                                    "Enable your dev, IT ops, and business teams to deliver great service experiences"
                            })}
                            headerType="h3"
                            ctaProps={{
                                primaryLinkProps: {
                                    text: intl.formatMessage({
                                        id: "homepage.carousel.itsm-cta",
                                        defaultMessage:
                                            "Deliver at high velocity"
                                    }),
                                    href: useLocalizedHref("/solutions/itsm")
                                }
                            }}
                        />
                        {itsmProducts.map((props, index) => (
                            <ProductDescription {...props} key={index} />
                        ))}
                    </div>
                    <div
                        css={rightColumnCss}
                        ref={itsmRef}
                        className={itsmClass}
                    >
                        <Solutions_2_JSM />
                    </div>
                </Row>
            )
        },
        {
            textLabel: intl.formatMessage({
                id: "homepage.carousel.tab-label.ado",
                defaultMessage: "Agile and DevOps"
            }),
            id: "agile-and-devops",
            component: (
                <Row layout="4-8">
                    <div css={leftCardCss} ref={adoRef} className={adoClass}>
                        <Card
                            bodyText=""
                            headerText={intl.formatMessage({
                                id: "homepage.carousel.ado-header",
                                defaultMessage:
                                    "Run a world-class agile software company—from discovery to delivery and operations"
                            })}
                            headerType="h3"
                            ctaProps={{
                                primaryLinkProps: {
                                    text: intl.formatMessage({
                                        id: "homepage.carousel.ado-cta",
                                        defaultMessage: "Explore Open DevOps"
                                    }),
                                    href: useLocalizedHref("/solutions/devops")
                                }
                            }}
                        />
                        {adoProducts.map((props, index) => (
                            <ProductDescription {...props} key={index} />
                        ))}
                    </div>
                    <div css={rightColumnCss} ref={adoRef} className={adoClass}>
                        <Solutions_3_Jira_JiraAlign_Compass />
                    </div>
                </Row>
            )
        }
    ];

    return (
        <UFOSegment name="carousel">
            <div css={carouselCss}>
                <h2>
                    {intl.formatMessage({
                        id: "homepage.carousel.header",
                        // Must use unicode characters for react-intl instead of "&nbsp;".
                        // This controls the word wrapping when resizing.
                        defaultMessage:
                            "Atlassian solutions are designed for\u00a0all\u00a0types\u00a0of\u00a0work"
                    })}
                </h2>
                <TabGroup
                    tabContentType="custom"
                    tabs={carouselTabs}
                    id="carouseltabs"
                    colors={carouselTabColors}
                    label={intl.formatMessage({
                        id: "homepage.carousel.tabs-label",
                        defaultMessage: "Tabs selector"
                    })}
                    dataProductKey={"home"}
                />
            </div>
        </UFOSegment>
    );
};
